import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, EMPTY, forkJoin, map, merge, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { AboutOmanCard, BackdropCard, BlinkProductionCard, ClientLogoCard, DEFAULT_GET_IN_TOUCH, FixerServiceCard, GetInTouchContent, OnSetMomentCard, SceneCard, ServiceCard, ShowreelHighlightCard, WhatWeHandleCard, WhyBlinkCard, WordpressAboutOmanItem, WordpressBackdropItem, WordpressBlinkProductionItem, WordpressClientItem, WordpressClientLogo, WordpressFixerServiceItem, WordpressGetInTouchItem, WordpressOnSetMomentItem, WordpressSceneItem, WordpressServiceItem, WordpressShowreelHighlightItem, WordpressWhatWeHandleItem, WordpressWhyBlinkItem } from '../models/service.model';

@Injectable({
  providedIn: 'root'
})
export class WordpressService {
  private readonly apiUrl = 'https://blink-productions.com/cms/wp-json/wp/v2/services';
  private readonly clientsApiUrl = 'https://blink-productions.com/cms/wp-json/wp/v2/our-clients';
  private readonly blinkProductionsApiUrl = 'https://blink-productions.com/cms/wp-json/wp/v2/blink-productions';
  private readonly whyBlinkApiUrl = 'https://blink-productions.com/cms/wp-json/wp/v2/why-blink';
  private readonly fixerServicesApiUrl = 'https://blink-productions.com/cms/wp-json/wp/v2/fixer-services';
  private readonly scenesApiUrl = 'https://blink-productions.com/cms/wp-json/wp/v2/scenes-you-can-build';
  private readonly showreelHighlightsApiUrl = 'https://blink-productions.com/cms/wp-json/wp/v2/showreel-highlights';
  private readonly onSetMomentsApiUrl = 'https://blink-productions.com/cms/wp-json/wp/v2/on-set-moments';
  private readonly getInTouchApiUrl = 'https://blink-productions.com/cms/wp-json/wp/v2/get-in-touch';
  private readonly backdropsApiUrl = 'https://blink-productions.com/cms/wp-json/wp/v2/backdrops';
  private readonly whatWeHandleApiUrl = 'https://blink-productions.com/cms/wp-json/wp/v2/what-we-handle';
  private readonly aboutOmanApiUrl = 'https://blink-productions.com/cms/wp-json/wp/v2/about-oman';

  constructor(private http: HttpClient) {}

  /**
   * Stale-while-revalidate: if a previous result for this key is cached in
   * sessionStorage, emit it immediately (so the UI can paint real content on the
   * very next reload instead of sitting on a skeleton), then still run the real
   * request in the background and emit its result too, refreshing both the UI and
   * the cache. On a true first load (nothing cached yet) this is just the plain
   * request — there is no way to skip that first round trip without showing stale
   * or fake data, so the very first visit in a tab still waits on WordPress.
   * If the background refresh fails while cached data is already on screen, the
   * error is swallowed rather than replacing good content with an error message;
   * it only surfaces when there's no cached fallback to fall back to.
   */
  private withCache<T>(cacheKey: string, request$: Observable<T[]>): Observable<T[]> {
    const storageKey = `wp-cache:${cacheKey}`;
    let cached: T[] | null = null;
    try {
      const raw = sessionStorage.getItem(storageKey);
      cached = raw ? (JSON.parse(raw) as T[]) : null;
    } catch {
      cached = null;
    }

    const live$ = request$.pipe(
      tap(result => {
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(result));
        } catch {
          // Caching is a pure optimization — ignore quota/availability issues.
        }
      }),
      catchError(error => (cached ? EMPTY : throwError(() => error)))
    );

    return cached ? merge(of(cached), live$) : live$;
  }

  getServices(): Observable<ServiceCard[]> {
    return this.withCache('services', this.fetchServices());
  }

  private fetchServices(): Observable<ServiceCard[]> {
    // acf_format=standard resolves acf.service_icon (an ACF image field) to a direct image
    // URL instead of a raw attachment ID, same as client_logo/production_item_image/etc.
    const baseParams = new HttpParams().set('_embed', 'true').set('per_page', '100').set('page', '1').set('acf_format', 'standard');

    return this.http.get<WordpressServiceItem[]>(this.apiUrl, { params: baseParams, observe: 'response' }).pipe(
      switchMap(response => {
        const body = response.body || [];
        const totalPages = Number(response.headers.get('X-WP-TotalPages') || '1');

        if (totalPages <= 1) {
          return of(body);
        }

        const requests = [] as Observable<WordpressServiceItem[]>[];
        for (let page = 2; page <= totalPages; page++) {
          const params = baseParams.set('page', String(page));
          requests.push(this.http.get<WordpressServiceItem[]>(this.apiUrl, { params }));
        }

        return forkJoin(requests).pipe(
          map(results => results.reduce((all, pageItems) => [...all, ...pageItems], body))
        );
      }),
      map(items => items.map(item => this.mapServiceItem(item))),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  /* Deliberately NOT wrapped in withCache (unlike the other sections): investigation into
     reports of WordPress edits/adds/deletes not showing up traced to withCache's
     stale-while-revalidate sessionStorage layer being a plausible, if intermittent, culprit
     — it emits a possibly-stale cached array first and only replaces it once the live
     request resolves, and if that live request ever errors while cached data exists,
     withCache's catchError silently swallows the error (by design, to avoid clobbering good
     content with an error state) and the stale array is left on screen with no indication a
     refresh even failed. For a small logo list where "always current" matters more than
     shaving one round trip, a plain fresh GET removes that failure mode entirely: every
     load/refresh is guaranteed to either show real current WordPress data or the existing
     error state — never silently-stale data. */
  getClients(): Observable<ClientLogoCard[]> {
    return this.fetchClients();
  }

  private fetchClients(): Observable<ClientLogoCard[]> {
    const baseParams = new HttpParams()
      .set('per_page', '100')
      .set('page', '1')
      .set('status', 'publish')
      .set('acf_format', 'standard');

    return this.http.get<WordpressClientItem[]>(this.clientsApiUrl, { params: baseParams, observe: 'response' }).pipe(
      switchMap(response => {
        const body = response.body || [];
        const totalPages = Number(response.headers.get('X-WP-TotalPages') || '1');

        if (totalPages <= 1) {
          return of(body);
        }

        const requests = [] as Observable<WordpressClientItem[]>[];
        for (let page = 2; page <= totalPages; page++) {
          const params = baseParams.set('page', String(page));
          requests.push(this.http.get<WordpressClientItem[]>(this.clientsApiUrl, { params }));
        }

        return forkJoin(requests).pipe(
          map(results => results.reduce((all, pageItems) => [...all, ...pageItems], body))
        );
      }),
      map(items => items.reduce((all: ClientLogoCard[], item) => all.concat(this.mapClientItem(item)), [] as ClientLogoCard[]).filter((item): item is ClientLogoCard => item !== undefined)),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  getBlinkProductions(): Observable<BlinkProductionCard[]> {
    return this.withCache('blink-productions', this.fetchBlinkProductions());
  }

  private fetchBlinkProductions(): Observable<BlinkProductionCard[]> {
    // acf_format=standard resolves acf.production_item_image to a direct image URL
    // instead of a raw attachment ID, same as why-blink/fixer-services/scenes.
    // orderby=date&order=asc: same reasoning as those sections — the API defaults to
    // newest-first, which reverses the intended reading order (Direction, About Us,
    // Philosophy match their creation order ascending).
    const baseParams = new HttpParams()
      .set('per_page', '100')
      .set('page', '1')
      .set('acf_format', 'standard')
      .set('orderby', 'date')
      .set('order', 'asc');

    return this.http.get<WordpressBlinkProductionItem[]>(this.blinkProductionsApiUrl, { params: baseParams, observe: 'response' }).pipe(
      switchMap(response => {
        const body = response.body || [];
        const totalPages = Number(response.headers.get('X-WP-TotalPages') || '1');

        if (totalPages <= 1) {
          return of(body);
        }

        const requests = [] as Observable<WordpressBlinkProductionItem[]>[];
        for (let page = 2; page <= totalPages; page++) {
          const params = baseParams.set('page', String(page));
          requests.push(this.http.get<WordpressBlinkProductionItem[]>(this.blinkProductionsApiUrl, { params }));
        }

        return forkJoin(requests).pipe(
          map(results => results.reduce((all, pageItems) => [...all, ...pageItems], body))
        );
      }),
      map(items => items.map(item => this.mapBlinkProductionItem(item))),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  getWhyBlink(): Observable<WhyBlinkCard[]> {
    return this.withCache('why-blink', this.fetchWhyBlink());
  }

  private fetchWhyBlink(): Observable<WhyBlinkCard[]> {
    // acf_format=standard resolves the icon field to a direct image URL instead of a
    // raw attachment ID, same as how our-clients resolves client_logo.
    // orderby=date&order=asc: the REST API's default is newest-first, which reverses
    // the intended reading order. Sorting by creation date ascending instead — rather
    // than sorting/filtering by title client-side — means whichever item is *created*
    // first in WordPress renders first, so editors control order by creation order
    // without any hardcoded title matching in the app.
    const baseParams = new HttpParams()
      .set('per_page', '100')
      .set('page', '1')
      .set('acf_format', 'standard')
      .set('orderby', 'date')
      .set('order', 'asc');

    return this.http.get<WordpressWhyBlinkItem[]>(this.whyBlinkApiUrl, { params: baseParams, observe: 'response' }).pipe(
      switchMap(response => {
        const body = response.body || [];
        const totalPages = Number(response.headers.get('X-WP-TotalPages') || '1');

        if (totalPages <= 1) {
          return of(body);
        }

        const requests = [] as Observable<WordpressWhyBlinkItem[]>[];
        for (let page = 2; page <= totalPages; page++) {
          const params = baseParams.set('page', String(page));
          requests.push(this.http.get<WordpressWhyBlinkItem[]>(this.whyBlinkApiUrl, { params }));
        }

        return forkJoin(requests).pipe(
          map(results => results.reduce((all, pageItems) => [...all, ...pageItems], body))
        );
      }),
      map(items => items.map(item => this.mapWhyBlinkItem(item))),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  getFixerServices(): Observable<FixerServiceCard[]> {
    return this.withCache('fixer-services', this.fetchFixerServices());
  }

  private fetchFixerServices(): Observable<FixerServiceCard[]> {
    // orderby=date&order=asc: same reasoning as why-blink — the API defaults to
    // newest-first, which reverses the intended order, so sort by creation date
    // ascending instead of hardcoding/matching titles client-side.
    // acf_format=standard: resolves acf.icon to a direct image URL instead of a
    // raw attachment ID, same as our-clients/why-blink.
    const baseParams = new HttpParams()
      .set('per_page', '100')
      .set('page', '1')
      .set('acf_format', 'standard')
      .set('orderby', 'date')
      .set('order', 'asc');

    return this.http.get<WordpressFixerServiceItem[]>(this.fixerServicesApiUrl, { params: baseParams, observe: 'response' }).pipe(
      switchMap(response => {
        const body = response.body || [];
        const totalPages = Number(response.headers.get('X-WP-TotalPages') || '1');

        if (totalPages <= 1) {
          return of(body);
        }

        const requests = [] as Observable<WordpressFixerServiceItem[]>[];
        for (let page = 2; page <= totalPages; page++) {
          const params = baseParams.set('page', String(page));
          requests.push(this.http.get<WordpressFixerServiceItem[]>(this.fixerServicesApiUrl, { params }));
        }

        return forkJoin(requests).pipe(
          map(results => results.reduce((all, pageItems) => [...all, ...pageItems], body))
        );
      }),
      map(items => items.map(item => this.mapFixerServiceItem(item))),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  getScenes(): Observable<SceneCard[]> {
    return this.withCache('scenes-you-can-build', this.fetchScenes());
  }

  private fetchScenes(): Observable<SceneCard[]> {
    // _embed=true resolves a standard WP featured image the same way "Our Services"
    // does; acf_format=standard resolves any ACF image field to a URL the same way
    // our-clients/why-blink/fixer-services do. orderby=date&order=asc: same reasoning
    // as the other sections — the API defaults to newest-first.
    const baseParams = new HttpParams()
      .set('_embed', 'true')
      .set('per_page', '100')
      .set('page', '1')
      .set('acf_format', 'standard')
      .set('orderby', 'date')
      .set('order', 'asc');

    return this.http.get<WordpressSceneItem[]>(this.scenesApiUrl, { params: baseParams, observe: 'response' }).pipe(
      switchMap(response => {
        const body = response.body || [];
        const totalPages = Number(response.headers.get('X-WP-TotalPages') || '1');

        if (totalPages <= 1) {
          return of(body);
        }

        const requests = [] as Observable<WordpressSceneItem[]>[];
        for (let page = 2; page <= totalPages; page++) {
          const params = baseParams.set('page', String(page));
          requests.push(this.http.get<WordpressSceneItem[]>(this.scenesApiUrl, { params }));
        }

        return forkJoin(requests).pipe(
          map(results => results.reduce((all, pageItems) => [...all, ...pageItems], body))
        );
      }),
      map(items => items.map(item => this.mapSceneItem(item))),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  getShowreelHighlights(): Observable<ShowreelHighlightCard[]> {
    return this.withCache('showreel-highlights', this.fetchShowreelHighlights());
  }

  private fetchShowreelHighlights(): Observable<ShowreelHighlightCard[]> {
    // _embed=true / acf_format=standard / orderby=date&order=asc: same reasoning as
    // the other sections (featured-image fallback, ACF image field resolved to a
    // URL, and the API's newest-first default reversed back to creation order).
    const baseParams = new HttpParams()
      .set('_embed', 'true')
      .set('per_page', '100')
      .set('page', '1')
      .set('acf_format', 'standard')
      .set('orderby', 'date')
      .set('order', 'asc');

    return this.http.get<WordpressShowreelHighlightItem[]>(this.showreelHighlightsApiUrl, { params: baseParams, observe: 'response' }).pipe(
      switchMap(response => {
        const body = response.body || [];
        const totalPages = Number(response.headers.get('X-WP-TotalPages') || '1');

        if (totalPages <= 1) {
          return of(body);
        }

        const requests = [] as Observable<WordpressShowreelHighlightItem[]>[];
        for (let page = 2; page <= totalPages; page++) {
          const params = baseParams.set('page', String(page));
          requests.push(this.http.get<WordpressShowreelHighlightItem[]>(this.showreelHighlightsApiUrl, { params }));
        }

        return forkJoin(requests).pipe(
          map(results => results.reduce((all, pageItems) => [...all, ...pageItems], body))
        );
      }),
      map(items => items.map(item => this.mapShowreelHighlightItem(item))),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  getOnSetMoments(): Observable<OnSetMomentCard[]> {
    return this.withCache('on-set-moments', this.fetchOnSetMoments());
  }

  private fetchOnSetMoments(): Observable<OnSetMomentCard[]> {
    // _embed=true / acf_format=standard / orderby=date&order=asc: same reasoning as
    // the other sections.
    const baseParams = new HttpParams()
      .set('_embed', 'true')
      .set('per_page', '100')
      .set('page', '1')
      .set('acf_format', 'standard')
      .set('orderby', 'date')
      .set('order', 'asc');

    return this.http.get<WordpressOnSetMomentItem[]>(this.onSetMomentsApiUrl, { params: baseParams, observe: 'response' }).pipe(
      switchMap(response => {
        const body = response.body || [];
        const totalPages = Number(response.headers.get('X-WP-TotalPages') || '1');

        if (totalPages <= 1) {
          return of(body);
        }

        const requests = [] as Observable<WordpressOnSetMomentItem[]>[];
        for (let page = 2; page <= totalPages; page++) {
          const params = baseParams.set('page', String(page));
          requests.push(this.http.get<WordpressOnSetMomentItem[]>(this.onSetMomentsApiUrl, { params }));
        }

        return forkJoin(requests).pipe(
          map(results => results.reduce((all, pageItems) => [...all, ...pageItems], body))
        );
      }),
      map(items => items.map(item => this.mapOnSetMomentItem(item))),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  getGetInTouch(): Observable<GetInTouchContent[]> {
    return this.withCache('get-in-touch', this.fetchGetInTouch());
  }

  private fetchGetInTouch(): Observable<GetInTouchContent[]> {
    // This is a singleton settings-style CPT — only ever one published entry — so
    // per_page=1 fetches it without any pagination handling.
    const baseParams = new HttpParams().set('per_page', '1').set('page', '1');

    return this.http.get<WordpressGetInTouchItem[]>(this.getInTouchApiUrl, { params: baseParams }).pipe(
      map(items => items.map(item => this.mapGetInTouchItem(item))),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  private mapGetInTouchItem(item: WordpressGetInTouchItem): GetInTouchContent {
    return {
      id: item.id,
      title: this.decodeHtml(item.acf?.contact_form_title || DEFAULT_GET_IN_TOUCH.title),
      namePlaceholder: this.decodeHtml(item.acf?.contact_name_placeholder || DEFAULT_GET_IN_TOUCH.namePlaceholder),
      emailPlaceholder: this.decodeHtml(item.acf?.contact_email_placeholder || DEFAULT_GET_IN_TOUCH.emailPlaceholder),
      phonePlaceholder: this.decodeHtml(item.acf?.contact_phone_placeholder || DEFAULT_GET_IN_TOUCH.phonePlaceholder),
      subjectPlaceholder: this.decodeHtml(item.acf?.contact_subject_placeholder || DEFAULT_GET_IN_TOUCH.subjectPlaceholder),
      messagePlaceholder: this.decodeHtml(item.acf?.contact_message_placeholder || DEFAULT_GET_IN_TOUCH.messagePlaceholder),
      submitButtonText: this.decodeHtml(item.acf?.contact_submit_button_text || DEFAULT_GET_IN_TOUCH.submitButtonText)
    };
  }

  getBackdrops(): Observable<BackdropCard[]> {
    return this.withCache('backdrops', this.fetchBackdrops());
  }

  private fetchBackdrops(): Observable<BackdropCard[]> {
    // orderby=date&order=asc: same reasoning as the other sections — the API
    // defaults to newest-first, which reverses the intended reading order (Coast &
    // Harbors, Desert Landscapes, Mountains & Wadis match their creation order
    // ascending).
    const baseParams = new HttpParams()
      .set('per_page', '100')
      .set('page', '1')
      .set('orderby', 'date')
      .set('order', 'asc');

    return this.http.get<WordpressBackdropItem[]>(this.backdropsApiUrl, { params: baseParams, observe: 'response' }).pipe(
      switchMap(response => {
        const body = response.body || [];
        const totalPages = Number(response.headers.get('X-WP-TotalPages') || '1');

        if (totalPages <= 1) {
          return of(body);
        }

        const requests = [] as Observable<WordpressBackdropItem[]>[];
        for (let page = 2; page <= totalPages; page++) {
          const params = baseParams.set('page', String(page));
          requests.push(this.http.get<WordpressBackdropItem[]>(this.backdropsApiUrl, { params }));
        }

        return forkJoin(requests).pipe(
          map(results => results.reduce((all, pageItems) => [...all, ...pageItems], body))
        );
      }),
      map(items => items.map(item => this.mapBackdropItem(item))),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  private mapBackdropItem(item: WordpressBackdropItem): BackdropCard {
    // acf.backdrop_title/acf.backdrop_description are what editors fill in via the
    // Custom Fields UI, so they must win over title.rendered (the native WP post
    // title) — same reasoning as scenes-you-can-build/why-blink/fixer-services/
    // showreel-highlights/blink-productions.
    const title = this.decodeHtml(item.acf?.backdrop_title || item.title?.rendered || 'Untitled Backdrop');
    const description = item.acf?.backdrop_description
      ? this.decodeHtml(item.acf.backdrop_description.trim()) || undefined
      : undefined;

    return { id: item.id, title, description };
  }

  getWhatWeHandle(): Observable<WhatWeHandleCard[]> {
    return this.withCache('what-we-handle', this.fetchWhatWeHandle());
  }

  private fetchWhatWeHandle(): Observable<WhatWeHandleCard[]> {
    // orderby=date&order=asc: same reasoning as the other sections — the API defaults
    // to newest-first, which reverses the intended reading order (verified live: the
    // items' creation order ascending matches the original "What We Handle" list order).
    const baseParams = new HttpParams()
      .set('per_page', '100')
      .set('page', '1')
      .set('acf_format', 'standard')
      .set('orderby', 'date')
      .set('order', 'asc');

    return this.http.get<WordpressWhatWeHandleItem[]>(this.whatWeHandleApiUrl, { params: baseParams, observe: 'response' }).pipe(
      switchMap(response => {
        const body = response.body || [];
        const totalPages = Number(response.headers.get('X-WP-TotalPages') || '1');

        if (totalPages <= 1) {
          return of(body);
        }

        const requests = [] as Observable<WordpressWhatWeHandleItem[]>[];
        for (let page = 2; page <= totalPages; page++) {
          const params = baseParams.set('page', String(page));
          requests.push(this.http.get<WordpressWhatWeHandleItem[]>(this.whatWeHandleApiUrl, { params }));
        }

        return forkJoin(requests).pipe(
          map(results => results.reduce((all, pageItems) => [...all, ...pageItems], body))
        );
      }),
      map(items => items.map(item => this.mapWhatWeHandleItem(item))),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  private mapWhatWeHandleItem(item: WordpressWhatWeHandleItem): WhatWeHandleCard {
    // Confirmed field name against the live API now that the ACF field group's location
    // rule is attached: acf.handle_item is what editors fill in via the Custom Fields UI,
    // so it must win over title.rendered — otherwise an ACF-only edit never reaches the
    // site because title.rendered stays on its old value (same class of fix already
    // applied for scenes-you-can-build/why-blink/fixer-services/showreel-highlights).
    // title.rendered is kept as a fallback for existing items created before the field
    // group was attached (acf.handle_item is empty "" on those until re-saved).
    const acfCandidate = (item.acf as { handle_item?: string } | undefined)?.handle_item;
    const titleCandidate = item.title?.rendered;
    const text = this.decodeHtml(
      (typeof acfCandidate === 'string' && acfCandidate.trim() ? acfCandidate.trim() : undefined)
        || (typeof titleCandidate === 'string' && titleCandidate.trim() ? titleCandidate.trim() : undefined)
        || item.content?.rendered?.replace(/<[^>]*>/g, '').trim()
        || ''
    );

    return { id: item.id, text };
  }

  getAboutOman(): Observable<AboutOmanCard[]> {
    return this.withCache('about-oman', this.fetchAboutOman());
  }

  private fetchAboutOman(): Observable<AboutOmanCard[]> {
    // orderby=date&order=asc: same reasoning as the other sections — the API defaults
    // to newest-first, which reverses the intended reading order (verified live: the
    // items' creation order ascending — Culture, Safety, Ease Of Work — matches the
    // original static section order).
    const baseParams = new HttpParams()
      .set('per_page', '100')
      .set('page', '1')
      .set('acf_format', 'standard')
      .set('orderby', 'date')
      .set('order', 'asc');

    return this.http.get<WordpressAboutOmanItem[]>(this.aboutOmanApiUrl, { params: baseParams, observe: 'response' }).pipe(
      switchMap(response => {
        const body = response.body || [];
        const totalPages = Number(response.headers.get('X-WP-TotalPages') || '1');

        if (totalPages <= 1) {
          return of(body);
        }

        const requests = [] as Observable<WordpressAboutOmanItem[]>[];
        for (let page = 2; page <= totalPages; page++) {
          const params = baseParams.set('page', String(page));
          requests.push(this.http.get<WordpressAboutOmanItem[]>(this.aboutOmanApiUrl, { params }));
        }

        return forkJoin(requests).pipe(
          map(results => results.reduce((all, pageItems) => [...all, ...pageItems], body))
        );
      }),
      map(items => items.map(item => this.mapAboutOmanItem(item))),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  private mapAboutOmanItem(item: WordpressAboutOmanItem): AboutOmanCard {
    // Confirmed field names against the live API response: acf.about_oman_title and
    // acf.about_oman_description are what editors fill in via the Custom Fields UI, so
    // they must win over title.rendered — otherwise an ACF-only edit never reaches the
    // site because title.rendered stays on its old value (same class of fix already
    // applied for scenes-you-can-build/why-blink/fixer-services/showreel-highlights).
    const title = this.decodeHtml(item.acf?.about_oman_title || item.title?.rendered || 'Untitled');
    const description = item.acf?.about_oman_description
      ? this.decodeHtml(item.acf.about_oman_description.trim()) || undefined
      : undefined;

    return { id: item.id, title, description };
  }

  private mapServiceItem(item: WordpressServiceItem): ServiceCard {
    // Verified against the live API + ACF field group registration: this post type has a
    // real "Service Title" text field (acf.service_title), separate from the native WP post
    // title (title.rendered) — the same two-title split already handled for blink-productions
    // and why-blink. The ACF field is what editors fill in via the Custom Fields UI, so it
    // must win; title.rendered is kept only as a fallback for items where it's empty.
    const titleCandidate = item.acf?.['service_title'];
    const title = this.decodeHtml(
      (typeof titleCandidate === 'string' && titleCandidate.trim() ? titleCandidate.trim() : undefined)
        || item.title?.rendered
        || 'Untitled Service'
    );
    const description = item.content?.rendered
      ? this.decodeHtml(item.content.rendered.replace(/<[^>]*>/g, '').trim())
      : undefined;
    const link = item.link || undefined;
    const imageUrl = this.resolveImageUrl(item);

    return {
      id: item.id,
      title,
      description,
      link,
      imageUrl,
      raw: item
    };
  }

  private resolveImageUrl(item: WordpressServiceItem): string | undefined {
    // Verified against the live API + ACF field group registration: acf.service_icon is a
    // real ACF image field (return_format: url), resolved to a direct URL string by
    // acf_format=standard above — this is the actual per-item icon/image editors set via
    // the Custom Fields UI, and must be the source of truth (no index/title-based
    // fallback: that hid the fact that every service actually carries its own image).
    const iconCandidate = item.acf?.['service_icon'];
    if (typeof iconCandidate === 'string' && iconCandidate.trim()) {
      return iconCandidate.trim();
    }

    // Generic WordPress featured-image mechanism, kept as a secondary (still
    // WordPress-sourced, not hardcoded) fallback for items using that instead of ACF.
    if (item._embedded?.['wp:featuredmedia']?.length) {
      const media = item._embedded['wp:featuredmedia'][0];
      return media.source_url || undefined;
    }

    return undefined;
  }

  private mapBlinkProductionItem(item: WordpressBlinkProductionItem): BlinkProductionCard {
    // Re-verified against the live API response: this post type's WordPress fields
    // were rebuilt with a proper ACF field group (acf.production_item_title,
    // acf.production_item_description, acf.production_item_image — the last resolved
    // to a direct URL by acf_format=standard, same as why-blink/fixer-services/scenes).
    // The ACF fields are what editors actually fill in via the Custom Fields UI, so
    // they must win over title.rendered/content.rendered — otherwise an ACF-only edit
    // never reaches the site (same class of bug fixed for scenes-you-can-build,
    // why-blink, fixer-services, and showreel-highlights). The old content-embedded
    // Gutenberg image/text parsing (from the earlier, ACF-less field structure) is
    // kept as a fallback only, in case a future item is ever missing the ACF fields.
    const title = this.decodeHtml(item.acf?.production_item_title || item.title?.rendered || 'Untitled');

    const descriptionCandidate = item.acf?.production_item_description;
    let description = typeof descriptionCandidate === 'string' && descriptionCandidate.trim()
      ? this.decodeHtml(descriptionCandidate.trim())
      : undefined;

    const imageCandidate = item.acf?.production_item_image;
    let imageUrl = typeof imageCandidate === 'string' && imageCandidate.trim() ? imageCandidate.trim() : undefined;
    let imageAlt = title;

    if (!imageUrl || !description) {
      const contentHtml = item.content?.rendered || '';
      const container = document.createElement('div');
      container.innerHTML = contentHtml;

      if (!imageUrl) {
        const img = container.querySelector('img');
        imageUrl = img?.getAttribute('src') || undefined;
        imageAlt = img?.getAttribute('alt') || title;
      }

      if (!description) {
        container.querySelector('figure')?.remove();
        description = container.textContent?.trim() || undefined;
      }
    }

    return { id: item.id, title, description, imageUrl, imageAlt };
  }

  private mapWhyBlinkItem(item: WordpressWhyBlinkItem): WhyBlinkCard {
    // This post type's ACF field group names every field after the first example
    // item ("clear_local_coordination..."), but the field keys are fixed across all
    // posts of this type — they hold the icon image, title text, and description
    // respectively for whichever post they belong to. The ACF field is what editors
    // actually fill in via the Custom Fields UI, so it must win over title.rendered
    // (the native WP post title) — otherwise an ACF-only edit never reaches the site
    // because title.rendered stays on its old value. title.rendered is kept only as
    // a fallback for the (currently theoretical) case where the ACF field is empty.
    const title = this.decodeHtml(item.acf?.clear_local_coordination || item.title?.rendered || 'Untitled');
    const description = item.acf?.clear_local_coordination_description
      ? this.decodeHtml(item.acf.clear_local_coordination_description.trim())
      : undefined;
    const iconCandidate = item.acf?.clear_local_coordination_icon;
    const iconUrl = typeof iconCandidate === 'string' && iconCandidate.trim() ? iconCandidate.trim() : undefined;

    return { id: item.id, title, description, iconUrl };
  }

  private mapFixerServiceItem(item: WordpressFixerServiceItem): FixerServiceCard {
    // Re-verified against the live API response: this post type now has real ACF
    // data — acf.description holds the description text (content.rendered stays
    // empty, it's genuinely not used by this post type), and acf.icon is an image
    // (resolved to a URL by acf_format=standard, same as why-blink's icon field) —
    // not a CSS class string. content.rendered is still checked as a fallback in
    // case a future item's description ends up there instead.
    // acf.service_title is what editors fill in via the Custom Fields UI, so it must
    // win over title.rendered (the native WP post title) — otherwise an ACF-only
    // edit never reaches the site because title.rendered stays on its old value.
    const title = this.decodeHtml(item.acf?.service_title || item.title?.rendered || 'Untitled Service');

    const descriptionSource = item.acf?.description || item.content?.rendered;
    const description = descriptionSource
      ? this.decodeHtml(descriptionSource.replace(/<[^>]*>/g, '').trim()) || undefined
      : undefined;

    const iconUrlCandidate = item.acf?.icon;
    const iconUrl = typeof iconUrlCandidate === 'string' && iconUrlCandidate.trim() ? iconUrlCandidate.trim() : undefined;

    // Fallback for any future item that has a CSS icon class instead of an image.
    const iconClassCandidate = item.acf?.icon_class || item.acf?.service_icon;
    const iconClass = !iconUrl && typeof iconClassCandidate === 'string' && iconClassCandidate.trim()
      ? iconClassCandidate.trim()
      : undefined;

    return { id: item.id, title, description, iconUrl, iconClass, slug: item.slug };
  }

  private mapSceneItem(item: WordpressSceneItem): SceneCard {
    // Confirmed field names now that "Show in REST API" is enabled for this field
    // group: acf.oman_scene_heading (title), acf.oman_scene_details (caption),
    // acf.oman_scene_visual (image, resolved to a URL by acf_format=standard the same
    // way our-clients/why-blink/fixer-services resolve their image fields).
    // acf.oman_scene_heading is what editors actually fill in via the Custom Fields
    // UI, so it must win over title.rendered (the native WP post title) — otherwise
    // an ACF-only edit never reaches the site because title.rendered stays on its old
    // value (confirmed live: editing oman_scene_heading alone left title.rendered
    // unchanged). title.rendered is kept only as a fallback for the case where the
    // ACF field is empty. The older speculative field-name guesses and the
    // featured-media/content-embedded-image fallbacks are kept below them, in case a
    // future item is ever missing oman_scene_visual/oman_scene_details for some reason.
    const title = this.decodeHtml(item.acf?.oman_scene_heading || item.title?.rendered || 'Untitled Scene');

    let imageUrl: string | undefined;
    const primaryImageCandidate = item.acf?.oman_scene_visual;
    if (typeof primaryImageCandidate === 'string' && primaryImageCandidate.trim()) {
      imageUrl = primaryImageCandidate.trim();
    }

    let imageAlt = title;
    if (!imageUrl) {
      imageUrl = item._embedded?.['wp:featuredmedia']?.[0]?.source_url || undefined;
      imageAlt = item._embedded?.['wp:featuredmedia']?.[0]?.alt_text || title;
    }

    const acfImageCandidate = item.acf?.image || item.acf?.scene_image || item.acf?.photo;
    if (!imageUrl && typeof acfImageCandidate === 'string' && acfImageCandidate.trim()) {
      imageUrl = acfImageCandidate.trim();
    }

    const contentHtml = item.content?.rendered || '';
    const container = document.createElement('div');
    container.innerHTML = contentHtml;

    if (!imageUrl) {
      const img = container.querySelector('img');
      if (img) {
        imageUrl = img.getAttribute('src') || undefined;
        imageAlt = img.getAttribute('alt') || imageAlt;
      }
    }

    // Caption: the short line shown under the title (e.g. "Expansive frames with
    // cinematic depth").
    let caption: string | undefined;
    if (item.acf?.oman_scene_details) {
      caption = this.decodeHtml(item.acf.oman_scene_details.trim()) || undefined;
    }

    if (!caption) {
      const acfCaptionCandidate = item.acf?.caption || item.acf?.tagline || item.acf?.subtitle || item.acf?.description;
      caption = typeof acfCaptionCandidate === 'string' && acfCaptionCandidate.trim()
        ? this.decodeHtml(acfCaptionCandidate.trim())
        : undefined;
    }

    if (!caption && contentHtml) {
      container.querySelector('figure')?.remove();
      container.querySelector('img')?.remove();
      const text = container.textContent?.trim();
      caption = text ? this.decodeHtml(text) : undefined;
    }

    return { id: item.id, title, caption, imageUrl, imageAlt };
  }

  private mapShowreelHighlightItem(item: WordpressShowreelHighlightItem): ShowreelHighlightCard {
    // Confirmed field names ("Show in REST API" enabled for this field group):
    // acf.showreel_commercial_heading (title), acf.showreel_commercial_visual
    // (thumbnail image, resolved to a URL by acf_format=standard), and
    // acf.showreel_commercial_video — a plain https://vimeo.com/<id> page URL,
    // NOT the player embed URL the existing markup's <iframe src> uses. The numeric
    // ID is extracted with a digits-only regex and interpolated into a URL template
    // that is otherwise fully hardcoded here (same background/autoplay/muted/loop/
    // byline/title/portrait params the static markup already used), so the embed
    // URL passed to bypassSecurityTrustResourceUrl in the component can only ever
    // be https://player.vimeo.com/video/<digits>?<fixed params> — never anything
    // attacker-controlled, regardless of what the raw WordPress field contains.
    // acf.showreel_commercial_heading is what editors fill in via the Custom Fields
    // UI, so it must win over title.rendered (the native WP post title) — otherwise
    // an ACF-only edit never reaches the site because title.rendered stays stale.
    const title = this.decodeHtml(item.acf?.showreel_commercial_heading || item.title?.rendered || 'Untitled Highlight');

    let imageUrl: string | undefined;
    const primaryImageCandidate = item.acf?.showreel_commercial_visual;
    if (typeof primaryImageCandidate === 'string' && primaryImageCandidate.trim()) {
      imageUrl = primaryImageCandidate.trim();
    }

    let imageAlt = title;
    if (!imageUrl) {
      imageUrl = item._embedded?.['wp:featuredmedia']?.[0]?.source_url || undefined;
      imageAlt = item._embedded?.['wp:featuredmedia']?.[0]?.alt_text || title;
    }

    const videoUrlCandidate = item.acf?.showreel_commercial_video;
    let vimeoUrl: string | undefined;
    let embedUrl: string | undefined;
    if (typeof videoUrlCandidate === 'string' && videoUrlCandidate.trim()) {
      vimeoUrl = videoUrlCandidate.trim();
      const match = vimeoUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (match) {
        embedUrl = `https://player.vimeo.com/video/${match[1]}?background=1&autoplay=1&muted=1&loop=1&byline=0&title=0&portrait=0`;
      }
    }

    return { id: item.id, title, imageUrl, imageAlt, vimeoUrl, embedUrl };
  }

  private mapOnSetMomentItem(item: WordpressOnSetMomentItem): OnSetMomentCard {
    // Confirmed field names ("Show in REST API" enabled for this field group):
    // acf.moment_image (image), acf.moment_description (the quote), acf.person_role
    // (the name/role line, e.g. "Executive Producer, Europe"). Note title.rendered
    // is NOT used here — this post type's actual WP post titles are meaningless
    // auto-generated slugs ("On Set Moments6" etc.), never intended for display;
    // person_role is the real "heading" shown on each card.
    const personRole = this.decodeHtml(item.acf?.person_role || 'Client');

    let imageUrl: string | undefined;
    const primaryImageCandidate = item.acf?.moment_image;
    if (typeof primaryImageCandidate === 'string' && primaryImageCandidate.trim()) {
      imageUrl = primaryImageCandidate.trim();
    }

    let imageAlt = personRole;
    if (!imageUrl) {
      imageUrl = item._embedded?.['wp:featuredmedia']?.[0]?.source_url || undefined;
      imageAlt = item._embedded?.['wp:featuredmedia']?.[0]?.alt_text || personRole;
    }

    const quoteSource = item.acf?.moment_description || item.content?.rendered;
    const quote = quoteSource
      ? this.decodeHtml(quoteSource.replace(/<[^>]*>/g, '').trim()) || undefined
      : undefined;

    return { id: item.id, imageUrl, imageAlt, quote, personRole };
  }

  private mapClientItem(item: WordpressClientItem): ClientLogoCard[] {
    const cards: ClientLogoCard[] = [];

    // First try the ACF field if present (real field name is "client_logo", confirmed
    // against the live ACF field group registration and a real wp-admin-created post —
    // "client_logo_1" never matched anything the API returns).
    const logo = item.acf?.client_logo;
    const imageUrl = this.resolveClientLogoUrl(logo);
    const title = this.decodeHtml(item.title?.rendered || 'Client');

    if (imageUrl) {
      const logoData = typeof logo === 'object' && logo !== null ? logo as WordpressClientLogo : undefined;
      const alt = logoData?.alt || logoData?.alt_text || `${title} logo`;
      cards.push({ id: item.id, title, imageUrl, alt });
      return cards;
    }

    // If ACF not available, parse images embedded in post content (legacy entries).
    // To avoid aggregate pages (which contain many logos) causing duplicates,
    // only convert content images into client cards when exactly one image is present.
    const contentHtml = item.content?.rendered || '';
    if (contentHtml) {
      const container = document.createElement('div');
      container.innerHTML = contentHtml;
      const imgs = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];

      if (imgs.length === 1) {
        const img = imgs[0];
        const src = img.getAttribute('src') || img.getAttribute('data-src') || '';
        if (src) {
          const alt = img.getAttribute('alt') || `${title} logo`;
          cards.push({ id: item.id, title, imageUrl: src, alt });
        }
      }
      // If multiple images are present, treat this post as an aggregate page and
      // do not create multiple client cards from it to avoid duplicates.
    }

    return cards;
  }

  private resolveClientLink(item: WordpressClientItem): string | undefined {
    const title = (item.title?.rendered || '').toLowerCase();

    if (/\bshell\b/.test(title)) {
      return 'https://www.shell.com.om/';
    }

    if (/ooredoo/.test(title)) {
      return 'https://www.ooredoo.om/';
    }

    if (/omantel/.test(title)) {
      return 'https://www.omantel.om/';
    }

    if (/\boq\b|oman oil|oman oil company|oman oil company/.test(title)) {
      return 'https://www.oq.com/';
    }

    if (/sultanate of oman|^oman$|^sultanate$|oman government/.test(title)) {
      return 'https://www.gov.om/';
    }

    return undefined;
  }

  private resolveClientLogoUrl(logo: WordpressClientItem['acf']['client_logo']): string | undefined {
    if (typeof logo === 'string' && logo.trim()) {
      return logo.trim();
    }

    if (typeof logo === 'number') {
      // If ACF returns an attachment ID, we cannot resolve here without extra requests.
      return undefined;
    }

    if (typeof logo === 'object' && logo !== null) {
      const logoData: any = logo;

      // Prefer explicitly provided full-size URL when available
      if (logoData.sizes) {
        // Common WP structure: sizes.full, sizes.large, sizes.medium
        if (logoData.sizes.full) {
          return logoData.sizes.full;
        }
        if (logoData.sizes.large) {
          return logoData.sizes.large;
        }
        if (logoData.sizes.medium) {
          return logoData.sizes.medium;
        }
      }

      // Fallback to common URL fields
      return logoData.url || logoData.source_url || logoData.src || undefined;
    }

    return undefined;
  }

  private decodeHtml(html: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    return textarea.value;
  }
}
